import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { UserHistoriesService } from '../_services/user-histories.service';
import { UserHistoryEventTypesService } from '../_services/user-history-event-types.service';
import { UserActivityMetric } from '../_types/user-activity-metric';
import { UserActivityHourly } from '../_types/user-activity-hourly';
import { UserHistory } from '../_types/user-history';
import { UserHistoryEventType } from '../_types/user-history-event-type';
import { UsersService } from '../../users/_services/users.service';
import { UserTypesService } from '../../users/_services/users-types.service';
import { FilterFieldConfig } from 'src/app/core/filter-panel/filter-field.model';

@Component({
    selector: 'app-users-activity-list',
    templateUrl: './users-activity-list.component.html',
    styleUrls: ['./users-activity-list.component.scss'],
    standalone: false
})
export class UsersActivityListComponent implements OnInit {

  public userHistories: UserHistory[] = [];
  public totalRecords: number = 0;
  public loading: boolean = false;
  private lastGridEvent: any;

  public filters: any = this.getDefaultFilters();

  //desplegables
  public users: any[] = [];
  public userTypes: any[] = [];
  public eventTypes: UserHistoryEventType[] = [];
  public modules: any[] = [];
  public submodules: any[] = [];
  public actions: any[] = [];
  public minutesOptions: any[] = [
    { value: null, name: 'Cualquiera' },
    { value: 5, name: 'Más de 5 minutos' },
    { value: 10, name: 'Más de 10 minutos' },
    { value: 20, name: 'Más de 20 minutos' }
  ];

  public userOptions: { label: string; value: any }[] = [];
  public userTypeOptions: { label: string; value: any }[] = [];
  public moduleOptions: { label: string; value: any }[] = [];
  public submoduleOptions: { label: string; value: any }[] = [];
  public actionOptions: { label: string; value: any }[] = [];
  public minutesFilterOptions: { label: string; value: any }[] = this.minutesOptions
    .filter(m => m.value != null)
    .map(m => ({ label: m.name, value: m.value }));
  public fieldsConfig: FilterFieldConfig[] = [
    { filter: this.filters.userId, type: 'multiselect', label: 'Usuario', options: this.userOptions, base: true },
    { filter: this.filters.moduleCode, type: 'select', label: 'Módulo', options: this.moduleOptions, base: true },
    { filter: this.filters.actionCode, type: 'select', label: 'Acción', options: this.actionOptions, filterable: true, base: true },
    { filter: this.filters.dateFrom, filterTo: this.filters.dateTo, type: 'date', label: 'Fecha', base: true },
    { filter: this.filters.userTypeCode, type: 'select', label: 'Tipo de usuario', options: this.userTypeOptions },
    { filter: this.filters.caseNumber, type: 'text', label: 'Expediente', placeholder: 'Código expediente' },
    { filter: this.filters.submoduleCode, type: 'select', label: 'Submódulo', options: this.submoduleOptions, filterable: true },
    { filter: this.filters.minutesFromLastAction, type: 'select', label: 'Minutos desde última acción', options: this.minutesFilterOptions }
  ];

  //gráficos
  public metrics: UserActivityMetric[] = [];
  public hourly: UserActivityHourly | null = null;
  public chartsLoading: boolean = false;
  public totalActions: number = 0;

  /// Agrupación de la tarta. Por módulo son cuatro porciones estables; con submódulo son más, y por eso
  /// a partir de la sexta se agrupan en "Otros": una tarta deja de leerse por encima de seis porciones.
  public groupByOptions: any[] = [
    { value: 'module', name: 'Módulo' },
    { value: 'submodule', name: 'Módulo + submódulo' }
  ];
  public groupBy: string = 'module';

  /// Con un rango de varios días, la media es lo que deja ver la jornada tipo; la suma sirve para el volumen.
  public hourlyModeOptions: any[] = [
    { value: 'average', name: 'Media por día' },
    { value: 'sum', name: 'Total del periodo' }
  ];
  public hourlyMode: string = 'average';

  public moduleChartData: any = null;
  public moduleChartOptions: any = null;
  public hourlyChartData: any = null;
  public hourlyChartOptions: any = null;

  /// Paleta categórica, validada para daltonismo y contraste. Se asignan en orden fijo y nunca se cicla:
  /// de la sexta categoría en adelante todo cae en "Otros", que va en gris.
  private static readonly CATEGORICAL = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'];
  private static readonly OTHER_COLOR = '#8a94a2';
  private static readonly MAX_SLICES = 5;

  /// Rampa de un solo tono para magnitud: más oscuro cuanto más alta la columna.
  private static readonly SEQUENTIAL_LOW = [158, 197, 244];
  private static readonly SEQUENTIAL_HIGH = [28, 92, 171];

  constructor(
    private userHistoriesService: UserHistoriesService,
    private userHistoryEventTypesService: UserHistoryEventTypesService,
    private usersService: UsersService,
    private userTypesService: UserTypesService,
    private topbarService: TopbarService,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit(): void {
    this.topbarService.setWindowTitle("Registro de actividad");
    this.topbarService.setTitle("Registro de actividad");

    //se llega desde la ficha de un usuario o de un operario con ?userId=
    const userId = this.route.snapshot.queryParamMap.get('userId');
    if (userId != null) {
      this.filters.userId.value = [userId];
      this.loadBreadcrumb(userId);
    }

    this.loadUsers();
    this.loadUserTypes();
    this.loadEventTypes();
    this.loadCharts();
  }

  /// Migas de pan colgando de la ficha del usuario del que se está viendo la actividad.
  async loadBreadcrumb(userId: string) {
    this.topbarService.addBreadcrumb("Usuarios", "/users");
    try {
      const user = await firstValueFrom(this.usersService.get(userId, { fields: { users: 'name' } }));
      if (user?.name) this.topbarService.addBreadcrumb(user.name, "/users/" + userId);
    } catch (error) {
      //sin el nombre las migas siguen siendo útiles, no merece la pena molestar al usuario
      console.warn('No se ha podido cargar el usuario para las migas de pan', error);
    }
  }

  getDefaultFilters() {
    const today = new Date();
    const dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);

    return {
      //any() admite varios usuarios a la vez
      userId: new Filter(FilterOperation.any, 'userId'),
      userTypeCode: new Filter(FilterOperation.equals, 'userTypeCode'),
      caseNumber: new Filter(FilterOperation.contains, 'case.caseNumber'),
      moduleCode: new Filter(FilterOperation.equals, 'eventType.moduleCode'),
      submoduleCode: new Filter(FilterOperation.equals, 'eventType.submoduleCode'),
      actionCode: new Filter(FilterOperation.equals, 'eventType.actionCode'),
      dateFrom: new Filter(FilterOperation.greaterOrEqual, 'date', '', dateFrom),
      dateTo: new Filter(FilterOperation.lessOrEqual, 'date'),
      minutesFromLastAction: new Filter(FilterOperation.greaterThan, 'minutesFromLastAction')
    };
  }

  public async loadData($event: any) {
    this.lastGridEvent = $event;
    this.loading = true;

    try {
      let params: IParamsCollection = {
        //ParamsBuilder espera el registro inicial que pasa p-table, no el número de página
        page: { number: $event.first, size: $event.rows },
        include: ['user', 'eventType', 'case'],
        //sin sparse fieldsets el include de user devolvería el usuario completo, con Password incluido
        fields: {
          users: 'name',
          cases: 'caseNumber',
          userHistoryEventTypes: 'moduleCode,moduleName,submoduleCode,submoduleName,actionCode,actionName'
        },
        sort: ['-date']
      };

      if (typeof $event.sortField != "undefined" && $event.sortField != null) {
        params.sort = [($event.sortOrder > 0 ? "" : "-") + $event.sortField];
      }

      params.filter = FilterBuilder.fromObject(this.filters).build();

      let response = await firstValueFrom(this.userHistoriesService.all(params));
      this.userHistories = response.data;
      this.totalRecords = response.meta['total'];
      this.topbarService.setCount(this.totalRecords);
    } finally {
      this.loading = false;
    }
  }

  async loadUsers() {
    let response = await firstValueFrom(this.usersService.all({ sort: ['name'], page: { size: 1000 }, fields: { users: 'name' } }));
    this.users = response.data;
    this.fillOptions(this.userOptions, this.users.map((user: any) => ({ label: user.name, value: user.id })));
  }

  async loadUserTypes() {
    let response = await firstValueFrom(this.userTypesService.all({ sort: ['name'], page: { size: 200 } }));
    this.userTypes = response.data.map((userType: any) => ({ code: userType.code, name: userType.name }));
    this.fillOptions(this.userTypeOptions, this.userTypes.map(m => ({ label: m.name, value: m.code })));
  }

  async loadEventTypes() {
    let response = await firstValueFrom(this.userHistoryEventTypesService.all({ sort: ['moduleName', 'submoduleName', 'actionName'], page: { size: 500 } }));
    this.eventTypes = response.data;

    this.modules = this.distinct(this.eventTypes.map(m => ({ code: m.moduleCode, name: m.moduleName })));
    this.actions = this.distinct(this.eventTypes.map(m => ({ code: m.actionCode, name: m.actionName })));
    this.fillOptions(this.moduleOptions, this.modules.map(m => ({ label: m.name, value: m.code })));
    this.fillOptions(this.actionOptions, this.actions.map(m => ({ label: m.name, value: m.code })));
    this.refreshSubmodules();
  }

  /// Los submódulos disponibles dependen del módulo seleccionado.
  refreshSubmodules() {
    const moduleCode = this.filters.moduleCode.value;
    const eventTypes = moduleCode ? this.eventTypes.filter(m => m.moduleCode == moduleCode) : this.eventTypes;

    this.submodules = this.distinct(
      eventTypes.filter(m => m.submoduleCode != null).map(m => ({ code: m.submoduleCode, name: m.submoduleName }))
    );
    this.fillOptions(this.submoduleOptions, this.submodules.map(m => ({ label: m.name, value: m.code })));

    //si el submódulo elegido ya no pertenece al módulo, lo limpiamos
    if (this.filters.submoduleCode.value && !this.submodules.some(m => m.code == this.filters.submoduleCode.value)) {
      this.filters.submoduleCode.value = null;
    }
  }

  onModuleChange() {
    this.refreshSubmodules();
    this.search();
  }

  async loadCharts() {
    this.chartsLoading = true;
    try {
      const dateFrom = this.filters.dateFrom.value ?? new Date(new Date().setHours(0, 0, 0, 0));
      const dateTo = this.filters.dateTo.value ?? new Date(new Date().setHours(23, 59, 59, 999));

      //los gráficos resumen un único usuario cuando hay uno solo seleccionado
      const selectedUsers = this.filters.userId.value;
      const userId = Array.isArray(selectedUsers) && selectedUsers.length == 1 ? selectedUsers[0] : null;
      const userTypeCode = this.filters.userTypeCode.value ?? null;

      const [metrics, hourly] = await Promise.all([
        firstValueFrom(this.userHistoriesService.getMetrics(dateFrom, dateTo, userId, userTypeCode)),
        firstValueFrom(this.userHistoriesService.getHourlyActivity(dateFrom, dateTo, userId, userTypeCode))
      ]);

      this.metrics = metrics;
      this.hourly = hourly;
    } catch (error) {
      //los gráficos son informativos: si fallan, la tabla debe seguir funcionando
      console.warn('No se han podido cargar los gráficos de actividad', error);
      this.metrics = [];
      this.hourly = null;
    } finally {
      this.chartsLoading = false;
      this.buildModuleChart();
      this.buildHourlyChart();
    }
  }

  /// Tarta de reparto. Agrupa por módulo o por módulo + submódulo según el selector, y a partir de la
  /// quinta categoría acumula el resto en "Otros" para que la tarta siga siendo legible.
  buildModuleChart() {
    const grouped = new Map<string, number>();
    this.metrics.forEach((metric) => {
      const name = this.groupBy == 'submodule' && metric.submoduleName
        ? metric.moduleName + ' · ' + metric.submoduleName
        : metric.moduleName;
      grouped.set(name, (grouped.get(name) ?? 0) + metric.count);
    });

    const sorted = Array.from(grouped.entries()).sort((a, b) => b[1] - a[1]);
    this.totalActions = sorted.reduce((sum, entry) => sum + entry[1], 0);

    const head = sorted.slice(0, UsersActivityListComponent.MAX_SLICES);
    const tail = sorted.slice(UsersActivityListComponent.MAX_SLICES);

    const labels = head.map((entry) => entry[0]);
    const values = head.map((entry) => entry[1]);
    const colors = head.map((_, i) => UsersActivityListComponent.CATEGORICAL[i]);

    if (tail.length > 0) {
      labels.push('Otros (' + tail.length + ')');
      values.push(tail.reduce((sum, entry) => sum + entry[1], 0));
      colors.push(UsersActivityListComponent.OTHER_COLOR);
    }

    const total = this.totalActions;
    const documentStyle = getComputedStyle(document.documentElement);
    const surface = documentStyle.getPropertyValue('--surface-card')?.trim() || '#ffffff';
    const textColor = documentStyle.getPropertyValue('--text-color')?.trim() || '#495057';

    this.moduleChartData = {
      labels: labels,
      datasets: [{
        data: values,
        backgroundColor: colors,
        //separación del color de la superficie entre porciones
        borderColor: surface,
        borderWidth: 2
      }]
    };

    this.moduleChartOptions = {
      cutout: '58%',
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            color: textColor,
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            pointStyle: 'rectRounded',
            //el valor va en la propia etiqueta: hay tonos que no llegan a 3:1 sobre el fondo y la
            //identidad no puede depender solo del color
            generateLabels: (chart: any) => {
              return chart.data.labels.map((label: string, i: number) => {
                const value = chart.data.datasets[0].data[i];
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return {
                  text: label + ' — ' + value + ' (' + pct + '%)',
                  fillStyle: chart.data.datasets[0].backgroundColor[i],
                  strokeStyle: chart.data.datasets[0].backgroundColor[i],
                  lineWidth: 0,
                  index: i
                };
              });
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const pct = total > 0 ? Math.round((context.parsed / total) * 100) : 0;
              return ' ' + context.label + ': ' + context.parsed + ' (' + pct + '%)';
            }
          }
        }
      }
    };
  }

  /// Columnas por hora del día. Una sola tinta, más oscura cuanto mayor el valor: es magnitud, no identidad.
  buildHourlyChart() {
    const counts = new Array(24).fill(0);
    if (this.hourly != null) {
      const divisor = this.hourlyMode == 'average' && this.hourly.days > 0 ? this.hourly.days : 1;
      this.hourly.hours.forEach((entry) => {
        if (entry.hour >= 0 && entry.hour < 24) counts[entry.hour] = entry.count / divisor;
      });
    }

    const max = Math.max(...counts);
    const isAverage = this.hourlyMode == 'average';
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color-secondary')?.trim() || '#6c757d';
    const gridColor = documentStyle.getPropertyValue('--surface-border')?.trim() || '#dee2e6';

    this.hourlyChartData = {
      labels: Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0')),
      datasets: [{
        data: counts.map((value) => isAverage ? Math.round(value * 10) / 10 : value),
        backgroundColor: counts.map((value) => this.sequentialColor(value, max)),
        borderRadius: 4,
        borderSkipped: 'bottom',
        barPercentage: 0.7,
        categoryPercentage: 0.9
      }]
    };

    this.hourlyChartOptions = {
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: any) => items[0].label + ':00 – ' + items[0].label + ':59',
            label: (context: any) => ' ' + context.parsed.y + (isAverage ? ' acciones de media' : ' acciones')
          }
        }
      },
      scales: {
        x: { ticks: { color: textColor }, grid: { display: false } },
        y: { beginAtZero: true, ticks: { color: textColor, precision: isAverage ? 1 : 0 }, grid: { color: gridColor } }
      }
    };
  }

  private sequentialColor(value: number, max: number): string {
    const t = max > 0 ? value / max : 0;
    const low = UsersActivityListComponent.SEQUENTIAL_LOW;
    const high = UsersActivityListComponent.SEQUENTIAL_HIGH;
    const channel = (i: number) => Math.round(low[i] + (high[i] - low[i]) * t);
    return 'rgb(' + channel(0) + ',' + channel(1) + ',' + channel(2) + ')';
  }

  /// Alternar la agrupación o el modo no vuelve al servidor: se recalcula con lo que ya está cargado.
  onGroupByChange() {
    this.buildModuleChart();
  }

  onHourlyModeChange() {
    this.buildHourlyChart();
  }

  /*
  clearFilters() {
    this.filters = this.getDefaultFilters();
    this.refreshSubmodules();
    this.search();
  }
  */

  search() {
    this.refreshSubmodules();
    if (this.lastGridEvent != null) {
      this.lastGridEvent.first = 0;
      this.loadData(this.lastGridEvent);
    }
    this.loadCharts();
  }

  /// Nombre legible del tipo de usuario, para no mostrar el código en crudo.
  userTypeName(code: string): string {
    if (!code) return '';
    const userType = this.userTypes.find(m => m.code == code);
    return userType != null ? userType.name : code;
  }

  private fillOptions(target: { label: string; value: any }[], source: { label: string; value: any }[]) {
    target.length = 0;
    target.push(...source);
  }

  private distinct(items: any[]): any[] {
    const result: any[] = [];
    items.forEach((item) => {
      if (item.code != null && !result.some(m => m.code == item.code)) result.push(item);
    });
    return result.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

}
